import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  CREATE_PROFESSIONAL_SERVICE,
  GET_SERVICES_BY_TYPE
} from '../graphql/professional-services';
import Modal from './Modal';

const TYPE_LABELS = {
  ATTORNEY: 'Attorney',
  BOND_ORIGINATOR: 'Bond Originator',
  CONTRACTOR: 'Contractor',
  INSPECTOR: 'Inspector',
  OTHER: 'Other'
};

const buildEmpty = () => ({
  companyName: '',
  primaryContact: { firstName: '', lastName: '', email: '', cellphone: '' },
  physicalAddress: { street: '', suburb: '', city: '' }
});

const QuickAddProfessionalServiceModal = ({ open, onClose, onCreated, type, title }) => {
  const [state, setState] = useState(buildEmpty);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setState(buildEmpty());
      setErrors({});
    }
  }, [open, type]);

  const [createService, { loading, error }] = useMutation(CREATE_PROFESSIONAL_SERVICE, {
    refetchQueries: [
      { query: GET_SERVICES_BY_TYPE, variables: { type, activeOnly: true } }
    ],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      onCreated?.(data?.createProfessionalService);
      setState(buildEmpty());
      setErrors({});
      onClose?.();
    },
    onError: (err) => console.error('Error creating professional service:', err)
  });

  const setField = (path, value) => {
    setState((prev) => {
      const parts = path.split('.');
      if (parts.length === 1) return { ...prev, [parts[0]]: value };
      return { ...prev, [parts[0]]: { ...prev[parts[0]], [parts[1]]: value } };
    });
    if (errors[path]) setErrors((prev) => ({ ...prev, [path]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!state.companyName.trim()) next.companyName = 'Required';

    const pc = state.primaryContact;
    if (!pc.firstName.trim()) next['primaryContact.firstName'] = 'Required';
    if (!pc.lastName.trim()) next['primaryContact.lastName'] = 'Required';
    if (!pc.email.trim()) next['primaryContact.email'] = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pc.email)) next['primaryContact.email'] = 'Invalid';
    if (!pc.cellphone.trim()) next['primaryContact.cellphone'] = 'Required';

    const addr = state.physicalAddress;
    if (!addr.street.trim()) next['physicalAddress.street'] = 'Required';
    if (!addr.suburb.trim()) next['physicalAddress.suburb'] = 'Required';
    if (!addr.city.trim()) next['physicalAddress.city'] = 'Required';

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    createService({
      variables: {
        inputData: {
          companyName: state.companyName.trim(),
          type,
          specializations: [],
          serviceAreas: [],
          primaryContact: {
            firstName: state.primaryContact.firstName.trim(),
            lastName: state.primaryContact.lastName.trim(),
            email: state.primaryContact.email.trim(),
            cellphone: state.primaryContact.cellphone.trim()
          },
          physicalAddress: {
            street: state.physicalAddress.street.trim(),
            suburb: state.physicalAddress.suburb.trim(),
            city: state.physicalAddress.city.trim()
          },
          active: true
        }
      }
    });
  };

  const inputClass = (k) =>
    `block w-full px-3 py-2 border ${errors[k] ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`;

  const heading = title || `Add ${TYPE_LABELS[type] || 'Professional Service'}`;

  return (
    <Modal open={open} onClose={onClose} title={heading} size="lg">
      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded text-sm">
          {error.message}
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Company Name*</label>
          <input
            type="text"
            value={state.companyName}
            onChange={(e) => setField('companyName', e.target.value)}
            className={inputClass('companyName')}
            autoFocus
          />
          {errors.companyName && <p className="mt-1 text-xs text-red-600">{errors.companyName}</p>}
        </div>

        <fieldset className="border border-gray-200 rounded-md p-3">
          <legend className="text-sm font-medium text-gray-700 px-1">Primary Contact</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">First Name*</label>
              <input
                type="text"
                value={state.primaryContact.firstName}
                onChange={(e) => setField('primaryContact.firstName', e.target.value)}
                className={inputClass('primaryContact.firstName')}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Last Name*</label>
              <input
                type="text"
                value={state.primaryContact.lastName}
                onChange={(e) => setField('primaryContact.lastName', e.target.value)}
                className={inputClass('primaryContact.lastName')}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Email*</label>
              <input
                type="email"
                value={state.primaryContact.email}
                onChange={(e) => setField('primaryContact.email', e.target.value)}
                className={inputClass('primaryContact.email')}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Cellphone*</label>
              <input
                type="tel"
                value={state.primaryContact.cellphone}
                onChange={(e) => setField('primaryContact.cellphone', e.target.value)}
                className={inputClass('primaryContact.cellphone')}
              />
            </div>
          </div>
        </fieldset>

        <fieldset className="border border-gray-200 rounded-md p-3">
          <legend className="text-sm font-medium text-gray-700 px-1">Physical Address</legend>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
            <div className="md:col-span-3">
              <label className="block text-xs text-gray-600 mb-1">Street*</label>
              <input
                type="text"
                value={state.physicalAddress.street}
                onChange={(e) => setField('physicalAddress.street', e.target.value)}
                className={inputClass('physicalAddress.street')}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Suburb*</label>
              <input
                type="text"
                value={state.physicalAddress.suburb}
                onChange={(e) => setField('physicalAddress.suburb', e.target.value)}
                className={inputClass('physicalAddress.suburb')}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">City*</label>
              <input
                type="text"
                value={state.physicalAddress.city}
                onChange={(e) => setField('physicalAddress.city', e.target.value)}
                className={inputClass('physicalAddress.city')}
              />
            </div>
          </div>
        </fieldset>

        <p className="text-xs text-gray-500">
          Only the essentials. Add VAT, license, banking and other details later from the Professional Services page.
        </p>

        <div className="flex justify-end space-x-2 pt-2 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Adding…' : `Add ${TYPE_LABELS[type] || 'Service'}`}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default QuickAddProfessionalServiceModal;
